import React, { useState } from 'react';

import cx from 'classnames';
import invariant from 'invariant';
import { keyBy } from 'lodash';

import { IconName } from 'components/IconDeprecated';
import Table from 'components/Table';
import TableRow from 'components/TableRow';
import Text from 'components/Text';

import getField, { Field } from './ComposableTableField';
import { getHeader } from './generators/Header';

import './styles/ComposableTable.scss';
import { SelectionState } from 'admin/rostering/lib/SelectionState';

type ColumnHeaderIcon = {
  iconName: IconName;
  tooltip: string;
};

export type ColumnHeader = {
  icon?: ColumnHeaderIcon;
  name: string;
  sortValue?: string;
  type?: 'avatar';
  // `width` of cell content not including sort icons, tooltipped icons, or cell padding
  width?: number;
  selectionMenu?: React.ReactElement;
  selectionState?: SelectionState;
  'data-testid'?: string;
};

export type ColumnGeneratorFunc<T> = (row: T) => any;
export type Props<T> = {
  className?: string;
  columnHeaders?: ColumnHeader[];
  columnGenerators: ColumnGeneratorFunc<T>[];
  rows: T[];
  keysOfActiveRows?: any[];
  keyField: Field<T>;
  actionsColumn?: boolean;
  actionsColumnShowOnHover?: boolean;
  handleSort?: Function;
  handleRowClick?: (arg0: T) => void;
  loading?: boolean;
  fixedHeader?: boolean;
  topOffset?: number;
  // `spreadsheet` adds column letters and row numbers
  // so that the table looks like a spreadsheet
  spreadsheet?: boolean;
  'data-testid'?: string;
  disableRowClick?: (row: T) => boolean;
  rowTooltip?: (row: T) => string | null;
};

export type SortDirection = 'asc' | 'desc';

// anything that Composable table needs to know about rows specifically,
// should go in here into this interface.
export interface GenericRowInterface {
  onClick?: () => void;
}

function ComposableTable<T extends GenericRowInterface | {}>(props: Props<T>) {
  const [sort, setSort] = useState<{ sortColumn?: string; sortDirection?: SortDirection }>({
    sortColumn: undefined,
    sortDirection: undefined,
  });

  const innerHandleSort = (sortValue: string, sortDirection: SortDirection) => {
    setSort({ sortColumn: sortValue, sortDirection });

    if (props.handleSort) {
      props.handleSort(sortValue, sortDirection);
    }
  };

  const generateHeaders = (columnHeaders: ColumnHeader[]): Array<React.ReactNode> => {
    const { topOffset, spreadsheet } = props;

    const generatedHeaders = columnHeaders.map((header, index) => {
      const sortProps = header.sortValue
        ? {
            onClick: innerHandleSort,
            sortColumn: sort.sortColumn,
            sortDirection: sort.sortDirection,
          }
        : undefined;

      return getHeader(header, index, sortProps, topOffset, spreadsheet);
    });

    if (props.spreadsheet) {
      // add an empty column header to accommodate the row numbers
      const emptyColumnHeader = (
        <th key="line-numbers" className="ComposableTableHeader" style={{ width: 20 }} />
      );
      return [emptyColumnHeader, ...generatedHeaders];
    }

    return generatedHeaders;
  };

  const buildRow = (row: T, index: number): JSX.Element[] => {
    const generatedRow = props.columnGenerators.map((generator, i) => {
      const key = i;
      return <td key={key}>{generator(row)}</td>;
    });

    if (props.spreadsheet) {
      // add a cell at the beginning of the row with the
      // row number
      const rowNumberCell = (
        <td key="line-number">
          <Text color="ruler">{index}</Text>
        </td>
      );
      return [rowNumberCell, ...generatedRow];
    }
    return generatedRow;
  };

  const renderTableBody = (keyField: Field<T>, rows: T[]): React.ReactElement[] => {
    const { handleRowClick, keysOfActiveRows, disableRowClick, rowTooltip } = props;

    const activeRowsKeyHash = keyBy(keysOfActiveRows, (key) => key);

    return rows.map((row, index) => {
      const isDisabled = disableRowClick && disableRowClick(row);
      const key = getField(row, keyField);
      const canHandleRowClick = !isDisabled && (!!handleRowClick || Boolean('onClick' in row));
      const tooltip = rowTooltip && rowTooltip(row);
      return (
        <TableRow
          hoverable={canHandleRowClick}
          // ref={setTriggerRef}
          onClick={() => {
            if (handleRowClick) {
              canHandleRowClick && handleRowClick(row);
            }
            if ('onClick' in row) {
              // this is not ideal, but it stops us from having to do subclassing in everything that uses composable table.
              // otherwise each row type would have to extend "GenericrRowInterface"
              (row as any).onClick();
            }
          }}
          tooltip={tooltip}
          key={key}
          active={key in activeRowsKeyHash}
        >
          {buildRow(row, index)}
        </TableRow>
      );
    });
  };

  const {
    actionsColumn,
    actionsColumnShowOnHover,
    className,
    columnGenerators,
    columnHeaders,
    keyField,
    handleSort,
    rows,
    fixedHeader,
    loading,
  } = props;

  if (columnHeaders) {
    invariant(
      columnGenerators.length === columnHeaders.length,
      "Won't have the same number of headers as columns!"
    );
    invariant(
      columnHeaders.some((header) => !!header.sortValue) ? handleSort : true,
      'Missing sortable function for sorting columns'
    );
  }

  return (
    <Table
      actionsColumn={actionsColumn}
      actionsColumnShowOnHover={actionsColumnShowOnHover}
      fixedHeader={fixedHeader}
      head={columnHeaders ? <tr>{generateHeaders(columnHeaders)}</tr> : null}
      loading={loading ?? !rows}
      className={cx('ComposableTable', className)}
      data-testid={props['data-testid']}
    >
      <tbody>{rows && renderTableBody(keyField, rows)}</tbody>
    </Table>
  );
}

export default ComposableTable;
